import { NoData } from "@/components/common/section-components/NoData";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { StandardTable } from "@/components/table/StandardTable";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FinancialMovementDesc,
  mfdFilterSchema,
} from "./schema/financial-movements-desc.schema";
import { MFDHeaderElements } from "./schema/header-elements";
import { FinancialMovementsDescriptionActions } from "@/components/common/section-components/action/FinancialMovementsDescriptionActions";
import { FieldValues, useForm } from "react-hook-form";
import {
  clearFiltersHandler,
  fetchFinancialMovementsDescHandler,
  handleDeleteFinancialMovementsDescriptionHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { FinancialMovementDescriptionFilter } from "@/components/common/section-components/filters/FinancialMovementsDescriptionFilter";
import { usePolling } from "@/hooks/usePolling";
import { zodResolver } from "@hookform/resolvers/zod";

export const FinancialMovementsDescPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, handleSubmit, getValues, setValue } = useForm({
    resolver: zodResolver(mfdFilterSchema),
  });

  const { userPermissions } = useRoles();
  const navigate = useNavigate();
  const handleAddFinancialMovementDesc = () => {
    navigate(AppRoutes.FINANCIAL_MOVEMENTS_DESC_ADD);
  };
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);

  const [loadingFinancialMovementDesc, setLoadingFinancialMovementDesc] =
    useState<boolean>(false);

  const [
    loadingDeleteFinancialMovementDesc,
    setLoadingDeleteFinancialMovementDesc,
  ] = useState(false);

  const [financialMovementsDesc, setFinancialMovementsDesc] = useState<FinancialMovementDesc[]>([]);

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<any>();

  const { fetchPermissions } = useRoles();

  const fetchFinancialMovementsDesc = async (fiters?: any, showLoad = true) => {
   await fetchFinancialMovementsDescHandler({
    setLoadingFinancialMovementDesc,
    setFinancialMovementsDesc,
    fiters,
    navigate,
    fetchPermissions,
    showLoad,
   })
  };
  const hasSelection = selection.length > 0;
  const indeterminate = Boolean(
    hasSelection &&
      financialMovementsDesc &&
      selection.length < financialMovementsDesc?.length,
  );

  useEffect(() => {
    if (isActive) fetchFinancialMovementsDesc();
  }, [isActive]);

  const handleEdit = (el: FinancialMovementDesc) => {
    navigate(
      AppRoutes.FINANCIAL_MOVEMENTS_DESC_EDIT.replace(
        ":id",
        String(el?.nroComprobante),
      ),
    );
  };

  const getActionElements = (item: FinancialMovementDesc) => (
    <FinancialMovementsDescriptionActions handleEdit={handleEdit} item={item} />
  );

  const clearFilters = () => {
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    submitFiltersHandler({
      event,
      setFiltersApplied,
      setOpenFilters,
      setCurrentFilters,
      fetchFinancialMovementsDesc,
    });
  };

  const handleDeleteFinancialMovementsDesc = async () => {
    handleDeleteFinancialMovementsDescriptionHandler({
      selection,
      setOpenConfirm,
      fetchFinancialMovementsDescription: fetchFinancialMovementsDesc,
      setLoadingDeleteFinancialMovementsDescription:
        setLoadingDeleteFinancialMovementDesc,
      setSelection,
    });
  };

  usePolling({
    callback: () => fetchFinancialMovementsDesc({ ...currentFilters }, false),
    delay: 10000,
    isActive,
  });

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.FINANCIAL_MOVEMENTS_DESC &&
        p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      data-testid="financial-movements-desc-page"
    >
      <SectionHeader
        title={`Relación comprobantes`}
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        handleAdd={handleAddFinancialMovementDesc}
        handleDelete={() => setOpenConfirm(true)}
        disableDelete={!selection?.length}
        loading={
          loadingFinancialMovementDesc || loadingDeleteFinancialMovementDesc
        }
        showButtons={hasWriteAccess}
      />

      <FinancialMovementDescriptionFilter
        control={control}
        handleSubmit={handleSubmit}
        clearFilters={clearFilters}
        submitFilters={submitFilters}
        setOpenFilters={setOpenFilters}
        openFilters={openFilters}
        filtersApplied={filtersApplied}
      />

      <Flex flexDir="column" marginTop="30px">
        {Boolean(
          !loadingFinancialMovementDesc && !financialMovementsDesc?.length,
        ) && <NoData />}
        {Boolean(
          !loadingFinancialMovementDesc && financialMovementsDesc?.length,
        ) && (
          <StandardTable
            rowId="nroComprobante"
            indeterminate={indeterminate}
            items={
              financialMovementsDesc?.map((pol: FinancialMovementDesc) => ({
                ...pol,
              })) ?? []
            }
            selection={selection}
            setSelection={setSelection}
            headers={MFDHeaderElements}
            noActions={!hasWriteAccess}
            noSelection={!hasWriteAccess}
            actionElements={getActionElements}
            actionsHeaderLabel="Edición"
            multipleSelection
          />
        )}
      </Flex>
      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirm}
          handleAccept={handleDeleteFinancialMovementsDesc}
          loadingAccept={loadingDeleteFinancialMovementDesc}
          title="Eliminar descripciones de movimientos"
          subtitle="¿Está seguro de que desea realizar esta acción?"
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
